/**
 * Storage backends. Small key-value data (settings, keymap) lives in localStorage; the preset
 * library lives in IndexedDB. Both are wrapped so the app keeps working when storage is
 * unavailable (private windows, blocked site data) and so tests can use in-memory backends.
 */
import { createStore, del, entries, get, set, type UseStore } from 'idb-keyval';

export interface SyncKV {
  get(key: string): unknown;
  set(key: string, value: unknown): void;
}

export interface AsyncKV {
  get<T>(key: string): Promise<T | undefined>;
  set(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
  entries<T>(): Promise<[string, T][]>;
}

export const localKV: SyncKV = {
  get(key) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : undefined;
    } catch {
      return undefined;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // unavailable: keep working without persistence
    }
  },
};

export function memoryKV(): SyncKV & AsyncKV {
  const m = new Map<string, unknown>();
  const clone = <T>(v: T): T => (v === undefined ? v : structuredClone(v));
  return {
    get: ((key: string) => clone(m.get(key))) as SyncKV['get'] & AsyncKV['get'],
    set: ((key: string, value: unknown) => {
      m.set(key, clone(value));
      return Promise.resolve();
    }) as SyncKV['set'] & AsyncKV['set'],
    delete: (key) => {
      m.delete(key);
      return Promise.resolve();
    },
    entries: <T>() => Promise.resolve([...m.entries()].map(([k, v]) => [k, clone(v) as T])),
  } as SyncKV & AsyncKV;
}

/** IndexedDB-backed store, falling back to memory if IndexedDB is unavailable. */
export function indexedKV(dbName: string, storeName: string): AsyncKV {
  let store: UseStore | null = null;
  let fallback: AsyncKV | null = null;
  try {
    if (typeof indexedDB !== 'undefined') store = createStore(dbName, storeName);
  } catch {
    store = null;
  }
  const mem = () => (fallback ??= memoryKV());
  const guard = async <T>(f: () => Promise<T>, alt: () => Promise<T>): Promise<T> => {
    if (!store) return alt();
    try {
      return await f();
    } catch {
      store = null;
      return alt();
    }
  };
  return {
    get: <T>(key: string) =>
      guard(
        () => get<T>(key, store!),
        () => mem().get<T>(key),
      ),
    set: (key, value) =>
      guard(
        () => set(key, value, store!),
        () => mem().set(key, value),
      ),
    delete: (key) =>
      guard(
        () => del(key, store!),
        () => mem().delete(key),
      ),
    entries: <T>() =>
      guard(
        async () => (await entries<string, T>(store!)) as [string, T][],
        () => mem().entries<T>(),
      ),
  };
}
